import { useEffect } from 'react'
import type { AbstractAgent } from '@copilotkit/react-core/v2'
import { useQueryClient } from '@tanstack/react-query'
import { useResultsStore } from '@editorStores'
import { PROJECT_CHANGED_EVENT, createLiveRunTracker } from '../../utils'
import { isRefreshedAfterProjectChange } from './utils'

// A step of the agent often sends several changes in a row; they are
// refreshed together, once the burst is over.
const REFRESH_DELAY_MS = 500

/**
 * Refresh what the app shows whenever the agent reports a change of the
 * project (the AG-UI custom event `project_changed`), so the scene follows
 * the agent's work.
 *
 * The query defaults are `staleTime: Infinity` and `refetchOnMount: false`,
 * so invalidating alone would leave the other Editor pages on their old
 * data: mounted queries are refetched, the rest are dropped and load fresh
 * when next used. Results are cleared first, as `useResultsInvalidation`
 * does -- a change usually resets them, and the result queries skip their
 * store writes once the backend answers "no results".
 *
 * Only live runs count: reconnecting to a long thread replays every earlier
 * `project_changed` at once, and refreshing for each would start hundreds of
 * full model loads in parallel (`createLiveRunTracker`).
 *
 * A subscription to the agent's event stream: the outside world this effect
 * exists for.
 */
const useRefreshOnProjectChange = (projectId: string, agent: AbstractAgent): void => {
  const queryClient = useQueryClient()

  useEffect(() => {
    const predicate = isRefreshedAfterProjectChange(projectId)
    const run = createLiveRunTracker()
    let pendingRefresh: ReturnType<typeof setTimeout> | undefined

    const refresh = () => {
      pendingRefresh = undefined
      useResultsStore.getState().clearForCheckReRun()
      queryClient.removeQueries({ type: 'inactive', predicate })
      queryClient.invalidateQueries({ predicate })
    }

    const subscription = agent.subscribe({
      ...run.callbacks,
      onCustomEvent: ({ event }) => {
        if (event.name !== PROJECT_CHANGED_EVENT || !run.isLive()) return
        clearTimeout(pendingRefresh)
        pendingRefresh = setTimeout(refresh, REFRESH_DELAY_MS)
      },
    })
    return () => {
      subscription.unsubscribe()
      clearTimeout(pendingRefresh)
    }
  }, [agent, projectId, queryClient])
}

export default useRefreshOnProjectChange
